import { type Moment } from "moment";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, useHistory, useRouteMatch } from "react-router";

import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { BTSelectItem, type IAPIHandlerResult } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";
import { MenuItemName } from "types/enum";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { showAPIErrorMessage } from "utilities/apiHandler";
import { isRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { getBaseRoute } from "utilities/routes";

import { BTAutoComplete } from "commonComponents/btWrappers/BTAutoComplete/BTAutoComplete";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconCloseOutlined, BTIconSearch } from "commonComponents/btWrappers/BTIcon";
import { Hotkey } from "commonComponents/utilities/Hotkey/Hotkey";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";
import {
    GlobalSearchHandler,
    type IGlobalSearchHandler,
} from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch.api.handler";
import {
    getSearchErrorMessage,
    type GlobalSearchEntity,
    type GlobalSearchFilters,
    type GlobalSearchResult,
    type IGlobalSearchError,
    type IGlobalSearchFilterValues,
    type IGlobalSearchOpenState,
    type IGlobalSearchState,
} from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch.types";
import { GlobalSearchDropdown } from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearchDropdown/GlobalSearchDropdown";
import {
    getInfoForSearchResponse,
    GetRecentlyViewedFromLocalStorage,
    ResetPageForJobNotInJobPicker,
    SetRecentlyViewedLocalStorage,
} from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearchUtils";
import {
    AllJobs,
    SearchCategory,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";
import { LinkType } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.utilities";
import { SearchBarRoutes } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBarRoutes";

import "./GlobalSearch.less";

interface IGlobalSearchProps {
    builderIdUserIdLookup: Record<number, number> | undefined;
    handler?: IGlobalSearchHandler;
    searchRequestedPage?: MenuItemName;
    handleHideSearchInput?: () => void;
    onFocus?: () => void;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent. Used to
     * flag-gate entity result links (bills, warranties) to navigate to a page
     * route instead of the relative modal route.
     */
    builderInfo?: BuilderInfo | null;
}

function getPathname(location: string | IRouteLocationState): string {
    if (isRouteLocationState(location)) {
        return location.pathname;
    }
    return location;
}

const MINIMUM_SEARCH_LENGTH = 2;

const defaultSearchHandler = new GlobalSearchHandler();
export const GlobalSearch: React.FunctionComponent<IGlobalSearchProps & ITrackingProp> = track({
    component: "Global Search",
})(({
    builderIdUserIdLookup,
    handler = defaultSearchHandler,
    searchRequestedPage = MenuItemName.None,
    tracking,
    handleHideSearchInput,
    onFocus,
    builderInfo,
}) => {
    // State
    const searchRequest = useRef<IAPIHandlerResult<GlobalSearchEntity> | undefined>(undefined);
    const [searchState, setSearchState] = useState<IGlobalSearchState>({
        selectedResultIndex: 0,
        isSearching: false,
        searchValue: "",
        performedAtLeastOneSuccessfulSearch: false,
    });
    const [filters, setFilters] = useState<GlobalSearchFilters | undefined>(undefined);
    const [selectedFilters, setSelectedFilters] = useState<IGlobalSearchFilterValues>({
        globalSearchJobId: AllJobs,
        globalSearchCategory: [],
    });
    const [openState, setOpenState] = useState<IGlobalSearchOpenState>({
        isOpen: false,
        showAllFilters: false,
    });

    // Ref
    const lastRecentlyViewedRefresh = useRef<Moment | null>(null);
    const searchTimestamp = useRef(0);
    const globalSearchOverlayRef = useRef<HTMLDivElement>(null);
    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);

    let searchOverlaytiming: number | null = null;

    const closeDropdown = useCallback(() => {
        if (searchOverlaytiming) {
            clearTimeout(searchOverlaytiming);
        }
        setOpenState({
            isOpen: false,
            showAllFilters: false,
        });
    }, [searchOverlaytiming]);

    const handleSearchOpen = () => {
        setOpenState({
            isOpen: true,
            showAllFilters: openState.showAllFilters,
        });
    };

    const handleSearchClose = useCallback(() => {
        closeDropdown();
        if (handleHideSearchInput !== undefined) {
            handleHideSearchInput();
        }
    }, [closeDropdown, handleHideSearchInput]);

    const openDropdown = async () => {
        if (!openState.isOpen) {
            if (
                !lastRecentlyViewedRefresh.current ||
                lastRecentlyViewedRefresh.current.diff(moment(), "minutes") > 5
            ) {
                await refreshRecentlyViewed();
            }

            searchOverlaytiming = setTimeout(handleSearchOpen, 250);
        }
    };

    useEffect(() => {
        if (!openState.isOpen) {
            window.removeEventListener("resize", closeDropdown);
        } else {
            window.addEventListener("resize", closeDropdown);
        }

        return () => {
            window.removeEventListener("resize", closeDropdown);
        };
    }, [openState.isOpen, closeDropdown]);

    // Computed State
    const userIds = useMemo(
        () => (builderIdUserIdLookup ? Object.values(builderIdUserIdLookup) : []),
        [builderIdUserIdLookup],
    );
    const selectedResultIndex = searchState.selectedResultIndex ?? -1;

    // Functions
    const handleNavdown = () => {
        if (!searchState || !searchState.results) return;
        if (searchState.selectedResultIndex < searchState.results.length - 1) {
            const newIndex = searchState.selectedResultIndex + 1;
            setSearchState({
                ...searchState,
                selectedResultIndex: newIndex,
            });
        }
    };

    const handleNavUp = () => {
        if (!searchState || !searchState.results) return;
        if (searchState.selectedResultIndex > 0) {
            const newIndex = searchState.selectedResultIndex - 1;
            setSearchState({
                ...searchState,
                selectedResultIndex: newIndex,
            });
        }
    };

    const handleMouseDown = useCallback(
        (e: MouseEvent) => {
            if (
                e.target instanceof Element &&
                !e.target?.matches(".GlobalSearch *") &&
                !e.target?.matches(".GlobalSearch")
            ) {
                closeDropdown();
            }
        },
        [closeDropdown],
    );

    const refreshRecentlyViewed = useCallback(async () => {
        lastRecentlyViewedRefresh.current = moment();
        const hasNoSearchTerm = searchState.searchValue === "";
        const recentResults = GetRecentlyViewedFromLocalStorage(userIds);
        if (recentResults.length === 0) {
            setSearchState({
                ...searchState,
                recentlyViewed: undefined,
            });
            return;
        }

        try {
            const resp = await handler.validateSearchItems(recentResults);
            const items = resp.searchItems.map(
                (r) =>
                    new BTSelectItem<GlobalSearchResult>({
                        id: r.id,
                        value: r.title,
                        extraData: r,
                    }),
            );
            if (hasNoSearchTerm) {
                setSearchState({
                    ...searchState,
                    recentlyViewed: items,
                    results: items,
                });
            } else {
                setSearchState({
                    ...searchState,
                    recentlyViewed: items,
                });
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    }, [handler, searchState, userIds]);

    useEffect(() => {
        BTLocalStorage.addChangeListener(
            "bt-objectArray-globalSearchRecentlyViewed",
            refreshRecentlyViewed,
        );
        return () =>
            BTLocalStorage.removeChangeListener(
                "bt-objectArray-globalSearchRecentlyViewed",
                refreshRecentlyViewed,
            );
    });

    const handleResultClick = async (item: GlobalSearchResult | undefined, resultRank?: number) => {
        if (!item || searchState.isSearching || searchState.error) {
            return;
        }
        const info = getInfoForSearchResponse(item, builderInfo ?? undefined);
        closeDropdown();

        if (info.link && searchState.results) {
            setSearchState({
                ...searchState,
                selectedResultIndex: 0,
            });

            tracking?.trackEvent({
                event: "LinkClick",
                element: "Link",
                uniqueId: SearchCategory[item.category],
                extraInfo: {
                    searchResultRank: resultRank,
                    searchResultId: item.id,
                    searchResultTitle: item.title,
                    searchQuery: searchState.searchValue,
                },
            });
            await ResetPageForJobNotInJobPicker(info, item.jobId);
            if (info.linkType === LinkType.WebformsLink) {
                window.location.assign(getPathname(info.link));
            } else if (info.linkType === LinkType.NewTabLink) {
                window.open(getPathname(info.link));
            } else if (info.linkType === LinkType.AutoSPARouting) {
                if (spaContext.isSpa) {
                    history.push(info.link);
                } else {
                    window.location.assign(getBaseRoute() + getPathname(info.link));
                }
            } else if (isRouteLocationState(info.link)) {
                history.push(info.link);
            } else {
                history.push(match.url + info.link);
            }
            if (builderIdUserIdLookup) {
                const userId = builderIdUserIdLookup[item.builderId];
                SetRecentlyViewedLocalStorage(userIds, userId, item);
            }
        }
    };

    const handleKeywordChange = () => {
        setSearchState({
            ...searchState,
            lastKeywordChange: moment(),
        });
    };

    const handleOnSearch = useCallback(
        async (searchValue: string, filterValues: IGlobalSearchFilterValues) => {
            searchRequest.current?.cancel();

            if (!openState.isOpen) {
                setOpenState({ ...openState, isOpen: true });
            }

            if (searchValue && searchValue.trim().length >= MINIMUM_SEARCH_LENGTH) {
                setSearchState({
                    ...searchState,
                    searchValue,
                    isSearching: true,
                });
                const categories =
                    filterValues.globalSearchCategory.length > 0 &&
                    filterValues.globalSearchCategory.length !== filters?.categorySelectItems.length
                        ? filterValues.globalSearchCategory
                        : undefined;
                searchTimestamp.current = performance.now();

                searchRequest.current = handler.get({
                    search: searchValue,
                    categories: categories,
                    jobIds:
                        filterValues.globalSearchJobId !== AllJobs
                            ? [filterValues.globalSearchJobId]
                            : undefined,
                    limit: 25,
                    requestedPage: searchRequestedPage,
                });

                try {
                    const entity = await searchRequest.current.response;
                    searchRequest.current = undefined;

                    let items: BTSelectItem<GlobalSearchResult>[] = [];
                    if (entity) {
                        items = entity.results.map(
                            (r) =>
                                new BTSelectItem<GlobalSearchResult>({
                                    id: r.id,
                                    value: r.title,
                                    extraData: r,
                                }),
                        );
                    }
                    setSearchState({
                        searchValue,
                        results: items,
                        error: undefined,
                        selectedResultIndex: 0,
                        isSearching: false,
                        recentlyViewed: searchState.recentlyViewed,
                        performedAtLeastOneSuccessfulSearch: true,
                    });
                    const durationMs = performance.now() - searchTimestamp.current;

                    tracking?.trackEvent({
                        event: "Search",
                        extraInfo: {
                            duration: `${durationMs} ms`,
                            query: searchValue,
                            ...(entity?.debug ?? {}),
                        },
                    });
                } catch (e) {
                    const error: IGlobalSearchError | undefined = getSearchErrorMessage(e);
                    if (!error.isAbortedRequest) {
                        setSearchState({
                            searchValue: searchValue,
                            results: undefined,
                            error: error,
                            selectedResultIndex: 0,
                            isSearching: false,
                            recentlyViewed: searchState.recentlyViewed,
                            performedAtLeastOneSuccessfulSearch:
                                searchState.performedAtLeastOneSuccessfulSearch,
                        });
                    }
                }
            } else {
                setSearchState({
                    searchValue,
                    results: searchState.recentlyViewed,
                    error: undefined,
                    selectedResultIndex: 0,
                    isSearching: false,
                    recentlyViewed: searchState.recentlyViewed,
                    performedAtLeastOneSuccessfulSearch:
                        searchState.performedAtLeastOneSuccessfulSearch,
                });
            }
        },
        [
            filters?.categorySelectItems.length,
            handler,
            openState,
            searchRequestedPage,
            searchState,
            tracking,
        ],
    );

    // Use Effects
    useEffect(() => {
        if (!openState.isOpen) {
            document.removeEventListener("mousedown", handleMouseDown);
        } else {
            document.addEventListener("mousedown", handleMouseDown);
        }

        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, [handleMouseDown, openState]);

    useEffect(() => {
        async function getFilters() {
            try {
                const filters = await handler.getFilters(searchRequestedPage);
                setFilters(filters);
            } catch (e) {
                showAPIErrorMessage(e);
            }
        }
        if (filters === undefined && openState.isOpen) {
            void getFilters();
        }
    }, [filters, handler, openState.isOpen, searchRequestedPage]);

    const globalSearchDropdown = () => (
        <GlobalSearchDropdown
            isSearching={searchState.isSearching ?? false}
            searchState={searchState}
            selectedResultIndex={selectedResultIndex}
            filters={filters}
            onFilterChange={async (filterValues) => {
                setSelectedFilters(filterValues);
                await handleOnSearch(searchState.searchValue ?? "", filterValues);
            }}
            selectedFilters={selectedFilters}
            onResultClick={handleResultClick}
            showAllFilters={openState.showAllFilters}
            onShowAllFilters={(value) =>
                setOpenState({
                    isOpen: true,
                    showAllFilters: value,
                })
            }
            builderInfo={builderInfo}
        />
    );

    const shouldBlurCloseDropdown = (e: React.FocusEvent<HTMLElement, Element>): boolean => {
        // listed below are all of the conditions that need to be met in order to close the dropdown
        return (
            !e.relatedTarget?.matches?.("#btnAllFilters") &&
            !e.relatedTarget?.matches?.("#btnApplyFilters") &&
            !e.target?.matches?.("#btnApplyFilters") &&
            !e.target?.matches?.("#btnResetFilters") &&
            !e.relatedTarget?.matches?.("#btnResetFilters") &&
            !e.relatedTarget?.matches?.("[id$=_Filter]") &&
            !e.target?.matches?.("[id$=_Filter]") &&
            ((e.target?.matches?.("[id^=rc_select]") && e.relatedTarget) ||
                !e.target?.matches?.("[id^=rc_select]")) &&
            !e.relatedTarget?.matches?.("#globalSearchJobId") &&
            !e.relatedTarget?.matches?.("#globalSearchCategory") &&
            !e.target?.matches?.("#globalSearchJobId") &&
            !e.target?.matches?.("#globalSearchCategory")
        );
    };

    return (
        <>
            <div
                data-testid="global-search-component"
                className={cn("GlobalSearch", {
                    "GlobalSearch-open": openState.isOpen,
                })}
                ref={globalSearchOverlayRef}
            >
                <Hotkey
                    {...HotkeyCommands["enter"]}
                    onCommand={async () => {
                        const lastKeywordChange = searchState.lastKeywordChange
                            ? moment
                                  .duration(moment().diff(searchState.lastKeywordChange))
                                  .asSeconds()
                            : 999;
                        const searchResult = searchState.results?.[selectedResultIndex];
                        if (lastKeywordChange > 1 && searchResult) {
                            await handleResultClick(
                                searchResult.extraData,
                                selectedResultIndex + 1,
                            );
                        }
                    }}
                    disabled={!openState.isOpen}
                    preventDefaultWhenDisabled={false}
                >
                    <Hotkey
                        {...HotkeyCommands["navDown"]}
                        onCommand={handleNavdown}
                        disabled={!openState.isOpen || searchState.results?.length === 0}
                        preventDefaultWhenDisabled={false} // both navDown/navUp: don't swallow arrow keys when closed — breaks rich text editors elsewhere
                    >
                        <Hotkey
                            {...HotkeyCommands["navUp"]}
                            onCommand={handleNavUp}
                            disabled={!openState.isOpen || searchState.results?.length === 0}
                            preventDefaultWhenDisabled={false}
                        >
                            <BTAutoComplete
                                onSearch={(searchPhrase) =>
                                    handleOnSearch(searchPhrase, selectedFilters)
                                }
                                placeholder="Search"
                                data-testid="globalSearchInput"
                                options={searchState.results}
                                notFoundContent={globalSearchDropdown}
                                className="SearchInput"
                                prefixIcon={<BTIconSearch size={16} />}
                                allowClear
                                clearButtonVisibleWithText
                                onClear={() => {
                                    searchRequest.current?.cancel();
                                    setSearchState({
                                        ...searchState,
                                        searchValue: "",
                                        isSearching: false,
                                        results: searchState.recentlyViewed,
                                        selectedResultIndex: 0,
                                    });
                                }}
                                dropdownRender={globalSearchDropdown}
                                open={openState.isOpen}
                                onFocus={async () => {
                                    onFocus?.();
                                    await openDropdown();
                                }}
                                onPrefixIconClick={openDropdown}
                                onClick={openDropdown}
                                onBlur={(e) => {
                                    if (shouldBlurCloseDropdown(e)) {
                                        closeDropdown();
                                    }
                                }}
                                onKeywordChange={handleKeywordChange}
                                hotkey="search"
                                hotkeyDisabled={openState.isOpen}
                                clearIcon={
                                    <BTIconCloseOutlined className="GlobalSearch" size={14} />
                                }
                            />
                        </Hotkey>
                    </Hotkey>
                </Hotkey>
                <BTButtonDeprecated
                    className="GlobalSearch-search-close MainNavDropdown MainNavDropdown-icon-only MainNavDropdown-search-close"
                    data-testid="search-close-main-nav-button"
                    aria-label="Search"
                    onClick={handleSearchClose}
                    noShadow
                >
                    <BTIconCloseOutlined className="MainNavDropdown-search-close--icon" />
                </BTButtonDeprecated>
            </div>
            <Route render={(routeProps) => <SearchBarRoutes {...routeProps} />} />
        </>
    );
});
